import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { getDashboardStats } from '@/services/api';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/DashboardLayout';
import { 
  Package, 
  FileText, 
  DollarSign, 
  Link as LinkIcon, 
  ArrowRight,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Settings
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getDashboardStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        toast.error('Erreur lors du chargement des statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: "Factures générées",
      value: stats?.total_invoices || 0,
      icon: FileText,
      color: "text-blue-600",
      bgColor: "bg-blue-100"
    },
    {
      title: "Revenus totaux",
      value: `${(stats?.total_revenue || 0).toFixed(2)} €`,
      icon: DollarSign,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100"
    }
  ];

  const integrations = [
    {
      name: "Back Market",
      connected: stats?.backmarket_connected,
      description: "API vendeur Back Market"
    },
    {
      name: "Pennylane",
      connected: stats?.pennylane_connected,
      description: "Facturation et comptabilité"
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Bonjour, {user?.company_name || 'Utilisateur'}
            </h1>
            <p className="text-slate-600 mt-1">
              Voici un aperçu de votre activité
            </p>
          </div>
          <div className="flex items-center gap-3">
            {user?.subscription_plan ? (
              <Badge className="bg-emerald-100 text-emerald-800 capitalize">
                Plan {user.subscription_plan}
              </Badge>
            ) : (
              <Link to="/subscription">
                <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="upgrade-btn">
                  Souscrire un abonnement
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">{stat.title}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">
                        {loading ? '...' : stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                      <stat.icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Integrations Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Intégrations
                    </CardTitle>
                    <CardDescription>
                      État de vos connexions API
                    </CardDescription>
                  </div>
                  <Link to="/settings">
                    <Button variant="outline" size="sm" data-testid="settings-link">
                      <Settings className="w-4 h-4 mr-2" />
                      Configurer
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {integrations.map((integration, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-lg border ${
                        integration.connected 
                          ? 'border-emerald-200 bg-emerald-50' 
                          : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg ${
                            integration.connected ? 'bg-emerald-500' : 'bg-slate-300'
                          } flex items-center justify-center`}>
                            <LinkIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{integration.name}</p>
                            <p className="text-xs text-slate-500">{integration.description}</p>
                          </div>
                        </div>
                        {loading ? (
                          <div className="w-6 h-6 rounded-full bg-slate-200 animate-pulse" />
                        ) : integration.connected ? (
                          <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                        ) : (
                          <XCircle className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {!loading && (!stats?.backmarket_connected || !stats?.pennylane_connected) && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      Configurez vos tokens API dans les paramètres pour accéder à toutes les fonctionnalités.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card className="border-slate-200 h-full">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Actions rapides
                </CardTitle>
                <CardDescription>
                  Accédez rapidement à vos outils
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/orders" className="block">
                  <Button variant="outline" className="w-full justify-start" data-testid="quick-orders-btn">
                    <Package className="w-4 h-4 mr-3" />
                    Voir les commandes
                  </Button>
                </Link>
                <Link to="/listings" className="block">
                  <Button variant="outline" className="w-full justify-start" data-testid="quick-listings-btn">
                    <TrendingUp className="w-4 h-4 mr-3" />
                    Gérer les listings
                  </Button>
                </Link>
                <Link to="/invoices" className="block">
                  <Button variant="outline" className="w-full justify-start" data-testid="quick-invoices-btn">
                    <FileText className="w-4 h-4 mr-3" />
                    Générer une facture
                  </Button>
                </Link>
                <Link to="/settings" className="block">
                  <Button variant="outline" className="w-full justify-start" data-testid="quick-settings-btn">
                    <Settings className="w-4 h-4 mr-3" />
                    Paramètres
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Getting Started Guide */}
        {!loading && (!stats?.backmarket_connected || !stats?.pennylane_connected) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Guide de démarrage
                </CardTitle>
                <CardDescription>
                  Configurez votre compte en quelques étapes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      stats?.backmarket_connected ? 'bg-emerald-500' : 'bg-slate-200'
                    }`}>
                      <span className="text-white font-medium text-sm">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Connectez Back Market</p>
                      <p className="text-sm text-slate-500">Ajoutez votre token API vendeur</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      stats?.pennylane_connected ? 'bg-emerald-500' : 'bg-slate-200'
                    }`}>
                      <span className="text-white font-medium text-sm">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Connectez Pennylane</p>
                      <p className="text-sm text-slate-500">Pour la génération de factures</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      user?.subscription_plan ? 'bg-emerald-500' : 'bg-slate-200'
                    }`}>
                      <span className="text-white font-medium text-sm">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Choisissez un plan</p>
                      <p className="text-sm text-slate-500">Débloquez toutes les fonctionnalités</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}
